import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../redux-hooks";
import { LoadingType } from "../../../types/CommonTypes";
import { submitPrescriptionService, searchPrescriptionsService, getRxRequiredOpenAccessProductsService, sendNotificationEmailService, logRxGatingBlockedAttemptService, logPrescriberConfirmationService, getGenesisIntakeRecordService, previewGenesisCardAuthorizationService } from "../../../service/PrescriptionService";
import { fetchPracticesByUserRole } from "../../../service/PracticeService";

export type NewPatient = {
  patientId: number | null;
  firstName: string;
  middleName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  phone: string;
  allergies: string | null;
  /** Patient Active Medication (e.g. Paracetamol) â€” sent on patientDataRequest. */
  patientActiveMedication: string | null;
  patientAddressInfo: {
    patientStreetAddress: string;
    patientUnitNumber: string;
    patientCity: string;
    patientState: string;
    patientZipCode: string;
  };
};

export type ProviderRequest = {
  providerId: number;
  npi: string;
  first_name: string;
  last_name: string;
  dea: string;
};

export type PracticeRequest = {
  practiceId: number;
  practiceLocationId: number;
  practiceName: string;
  phone: string;
  practiceAddressInfo: {
    streetAddress: string;
    unitNumber: string;
    city: string;
    state: string;
    zipCode: string;
  };
};

export type ProductPayload = {
  drugName: string;
  medicationNdc: string | null;
  externalId: string;
  prescribedItemId: number;
  quantity: number;
  unit: string;
  refills: number;
  directions: string;
  sig: string;
  notes: string;
};

/** Prescriber data captured before Rx submit (confirmation). */
export type PrescriberConfirmation = {
  confirmed: true;
  confirmedAt: string; // ISO timestamp
  providerId: number;
  providerName: string;
  npi: string;
  patientState: string;
  acknowledgmentText: string;
};

/**
 * Compliance audit payload for a Gate 1 (pharmacy ship-to) blocked attempt.
 * Backend first creates/updates a local prescription (status=BLOCKED), then
 * audits against that prescription id.
 */
export type RxGatingBlockedAttemptRequest = {
  entityName: "PRESCRIPTION";
  entityNumber: string;
  /** Local prescription id when known; otherwise null until the first blocked draft is created. */
  entityId: number | null;
  /** Reuse an existing BLOCKED draft from this wizard session. */
  prescriptionId?: number | null;
  actionType: "BLOCKED";
  action: string;
  auditTimestamp: string;
  auditedObject: {
    timestamp: string;
    userName: string | null;
    practiceId: number | null;
    patientId: number | null;
    locationId: number | null;
    productId: number | null;
    productName: string | null;
    pharmacy: {
      pharmacyId: number;
      pharmacyName: string;
    };
    prescriber: {
      providerId: number | null;
      providerName: string | null;
      npi: string | null;
    };
    state: string;
    shipTo: string | null;
    shipToPractice: boolean;
    compoundType: string;
    isSterileCompound: boolean;
    reasons: Array<{
      code: string;
      message: string;
      state?: string;
    }>;
  };
};

/**
 * Compliance audit payload for Gate 2 (prescriber confirmation) acknowledgment.
 * Backend creates/updates a local draft (PENDING, or keeps BLOCKED), then audits
 * against that prescription id before submit is allowed to proceed.
 */
export type LogPrescriberConfirmationRequest = {
  entityName: "PRESCRIPTION";
  entityNumber: string;
  entityId: number | null;
  prescriptionId?: number | null;
  actionType: "MODIFY";
  action: string;
  auditTimestamp: string;
  auditedObject: {
    timestamp: string;
    userName: string | null;
    practiceId: number | null;
    patientId: number | null;
    locationId: number | null;
    productId: number | null;
    productName: string | null;
    providerId: number | null;
    providerName: string | null;
    npi: string | null;
    patientState: string | null;
    acknowledgmentText: string;
    confirmed: boolean;
    confirmedAt: string;
  };
};

export type PrescriptionSubmitRequest = {
  productId: number;

  patientDataRequest: NewPatient;
  providerDateRequet: ProviderRequest;
  practiceDataRequest: PracticeRequest;

  productPayload: ProductPayload[];

  billTo: string;
  shipTo: string;

  notes: string | null;

  /** Audit trail: provider license attestation for patient state. */
  prescriberConfirmation: PrescriberConfirmation | null;

  /**
   * Local prescription id created when Gate 1 blocked this attempt.
   * Backend updates that same row to SUBMITTED on successful submit.
   */
  prescriptionId?: number | null;
};

export type SearchPrescriptionRequest = {
  practiceIds: number[];
  patientFirstName: string;
  patientLastName: string;
  providerIds: number[];
  prescriptionNumber: string;
  fromDate: string;
  toDate: string;
  status: string[];
  manufacturerId?: number | null;
  currentPage: number;
  itemsPerPage: number;
  sortBy?: "patientName" | "providerName" | "practiceName" | "manufacturerName" | "productName" | "status" | "createdAt";
  sortOrder?: "asc" | "desc";
};

export type PrescriptionListItem = {
  prescriptionId: number;
  integrationPrescriptionId: string;
  patientName: string;
  providerName: string;
  practiceName: string;
  practiceId: number;
  /** CS-2075 — pharmacy the Rx was submitted to. */
  manufacturerId: number | null;
  manufacturerName: string | null;
  productName: string;
  drugName: string;
  status: string;
  tracking: string | null;
  createdAt: string;
  documentId: number | null;
  responsePayload: string | null;
  batchId?: string | null;
};

export type PrescriptionListResponse = {
  content: PrescriptionListItem[];
  totalPages: number;
  totalElements: number;
};

export type PrescriptionPracticeInfo = {
  practiceId: number;
  practiceName: string;
};

export type RxOpenAccessProduct = {
  productId: number;
  productName: string;
  manufacturerId: number | null;
  manufacturerName: string | null;
  productCategory: string | null;
  composition: string | null;
  hcpcsCode: string | null;
  /** Product config AGREEMENT / MODE (MANDATORY, AUTO, NONE) */
  agreementMode?: string | null;
  /** From practice product agreement when practice is selected */
  extendedPrice?: number | string | null;
};

export type PrescriptionState = {
  submitPrescriptionStatus: LoadingType;
  prescriptionListInfo: PrescriptionListResponse | null;
  prescriptionListFetchStatus: LoadingType;
  practicesByUserRole: PrescriptionPracticeInfo[];
  practicesFetchStatus: LoadingType;
  rxOpenAccessProducts: RxOpenAccessProduct[];
  rxOpenAccessProductsFetchStatus: LoadingType;
  sendNotificationEmailStatus: LoadingType;
};

const initialState: PrescriptionState = {
  submitPrescriptionStatus: "idle",
  prescriptionListInfo: null,
  prescriptionListFetchStatus: "idle",
  practicesByUserRole: [],
  practicesFetchStatus: "idle",
  rxOpenAccessProducts: [],
  rxOpenAccessProductsFetchStatus: "idle",
  sendNotificationEmailStatus: "idle",
};


export const submitPrescription = createAsyncThunk<any, { request: PrescriptionSubmitRequest, skipLoader?: boolean }>("prescription/submit", async ({request, skipLoader= false}, { rejectWithValue }) => {
    try {
        const response = await submitPrescriptionService(request, { 'x-skip-loader': skipLoader.toString() });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error?.message || 'An error occurred');
    }
});

export const fetchPrescriptionList = createAsyncThunk<any, { data: SearchPrescriptionRequest; skipLoader?: boolean }>(
  "prescription/fetchList",
  async ({ data, skipLoader = false }, { rejectWithValue }) => {
    try {
      const response = await searchPrescriptionsService(data, skipLoader);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

export const sendNotificationEmail = createAsyncThunk<any, { providerId?: number; practiceId: number; skipLoader?: boolean }>(
  "prescription/sendNotificationEmail",
  async ({ providerId, practiceId, skipLoader = true }, { rejectWithValue }) => {
    try {
      const response = await sendNotificationEmailService(providerId, practiceId, skipLoader);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

export const fetchPracticesByUserRolePrescription = createAsyncThunk<any, { isActivePracticeOnly: boolean; skipLoader?: boolean }>(
  "prescription/fetchPractices",
  async ({ isActivePracticeOnly, skipLoader = true }, { rejectWithValue }) => {
    try {
    const response = await fetchPracticesByUserRole(isActivePracticeOnly, skipLoader ?? true);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

export const fetchRxRequiredOpenAccessProducts = createAsyncThunk<any, { skipLoader?: boolean } | void>(
  "prescription/fetchRxOpenAccessProducts",
  async (args, { rejectWithValue }) => {
    try {
      const skipLoader = args?.skipLoader ?? false;
      const response = await getRxRequiredOpenAccessProductsService(skipLoader);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

/** CS-2073 — Fire-and-forget compliance audit for Gate 1 blocked attempts. */
export const logRxGatingBlockedAttempt = createAsyncThunk<
  any,
  { request: RxGatingBlockedAttemptRequest; skipLoader?: boolean }
>("prescription/logRxGatingBlockedAttempt", async ({ request, skipLoader = true }, { rejectWithValue }) => {
  try {
    const response = await logRxGatingBlockedAttemptService(request, skipLoader);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.message || "An error occurred");
  }
});

/** CS-2073 — Must succeed before Gate 2 Continue proceeds to submit. */
export const logPrescriberConfirmation = createAsyncThunk<
  any,
  { request: LogPrescriberConfirmationRequest; skipLoader?: boolean }
>("prescription/logPrescriberConfirmation", async ({ request, skipLoader = true }, { rejectWithValue }) => {
  try {
    const response = await logPrescriberConfirmationService(request, skipLoader);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.message || "An error occurred");
  }
});

/**
 * CS-2075 — Values typed into the Genesis Credit Card Authorization popup. They are
 * sent once to fill the PDF template for preview and are never stored by V3.
 */
export type GenesisCardAuthorizationRequest = {
  cardholderName: string;
  cardNumber: string;
  expirationDate: string;
  cvv: string;
  billingAddress: string;
  signature: string;
  signedDate: string;
};

/** CS-2075 — Live AutoPilotRx intake JSON when opening Genesis Response JSON. */
export const fetchGenesisIntakeRecord = createAsyncThunk<
  any,
  { prescriptionId: number; skipLoader?: boolean }
>("prescription/fetchGenesisIntakeRecord", async ({ prescriptionId, skipLoader = false }, { rejectWithValue }) => {
  try {
    const response = await getGenesisIntakeRecordService(prescriptionId, skipLoader);
    const body = response.data;
    if (body?.code && body.code !== "V3OK") {
      return rejectWithValue(body.message || "Unable to load Genesis intake record");
    }
    return body?.data ?? body;
  } catch (error: any) {
    return rejectWithValue(error?.message || "An error occurred");
  }
});

/** CS-2075 — Returns the filled card authorization PDF as base64 for the preview viewer. */
export const previewGenesisCardAuthorization = createAsyncThunk<
  any,
  { request: GenesisCardAuthorizationRequest; skipLoader?: boolean }
>("prescription/previewGenesisCardAuthorization", async ({ request, skipLoader = false }, { rejectWithValue }) => {
  try {
    const response = await previewGenesisCardAuthorizationService(request, skipLoader);
    const body = response.data;
    if (body?.code && body.code !== "V3OK") {
      return rejectWithValue(body.message || "Unable to generate the authorization form");
    }
    return body?.data ?? body;
  } catch (error: any) {
    return rejectWithValue(error?.message || "An error occurred");
  }
});

const prescriptionSlice = createSlice({
  name: "prescriptionSlice",
  initialState,
  reducers: {
    resetPrescriptionListInfo: (state) => {
      state.prescriptionListInfo = null;
      state.prescriptionListFetchStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitPrescription.pending, (state) => {
        state.submitPrescriptionStatus = "loading";
      })
      .addCase(submitPrescription.fulfilled, (state) => {
        state.submitPrescriptionStatus = "success";
      })
      .addCase(submitPrescription.rejected, (state) => {
        state.submitPrescriptionStatus = "error";
      })
      // Prescription list
      .addCase(fetchPrescriptionList.pending, (state) => {
        state.prescriptionListFetchStatus = "loading";
      })
      .addCase(fetchPrescriptionList.fulfilled, (state, action) => {
        state.prescriptionListFetchStatus = "success";
        state.prescriptionListInfo = action.payload;
      })
      .addCase(fetchPrescriptionList.rejected, (state) => {
        state.prescriptionListFetchStatus = "error";
      })
      // Practices for prescription
      .addCase(fetchPracticesByUserRolePrescription.pending, (state) => {
        state.practicesFetchStatus = "loading";
      })
      .addCase(fetchPracticesByUserRolePrescription.fulfilled, (state, action) => {
        state.practicesFetchStatus = "success";
        // state.practicesByUserRole = action.payload;
        state.practicesByUserRole = Array.isArray(action.payload) ? action.payload : []; // CS-1984
      })
      .addCase(fetchPracticesByUserRolePrescription.rejected, (state) => {
        state.practicesFetchStatus = "error";
      })
      // Rx Open Access Products
      .addCase(fetchRxRequiredOpenAccessProducts.pending, (state) => {
        state.rxOpenAccessProductsFetchStatus = "loading";
      })
      .addCase(fetchRxRequiredOpenAccessProducts.fulfilled, (state, action) => {
        state.rxOpenAccessProductsFetchStatus = "success";
        state.rxOpenAccessProducts = action.payload || [];
      })
      .addCase(fetchRxRequiredOpenAccessProducts.rejected, (state) => {
        state.rxOpenAccessProductsFetchStatus = "error";
        state.rxOpenAccessProducts = [];
      })
      .addCase(sendNotificationEmail.pending, (state) => {
        state.sendNotificationEmailStatus = "loading";
      })
      .addCase(sendNotificationEmail.fulfilled, (state) => {
        state.sendNotificationEmailStatus = "success";
      })
      .addCase(sendNotificationEmail.rejected, (state) => {
        state.sendNotificationEmailStatus = "idle";
      });
  },
});

export const { resetPrescriptionListInfo } = prescriptionSlice.actions;

export const usePrescriptionSlice = () =>
  useAppSelector((state) => state.prescriptionSlice);

export default prescriptionSlice.reducer;